import env from "@/env.config";
import { useEffect, useRef } from "react";

type DeeplinkOptions = {
	/**
	 * Delay in milliseconds before considering the deep link attempt as failed.
	 * @default 1500
	 */
	delayMs?: number;

	/**
	 * If true, disables the deep link attempt.
	 * @default false
	 */
	disabled?: boolean;

	/**
	 * Called if the deep link fails (e.g. app not installed).
	 */
	onFailed?: () => void;

	/**
	 * Called if the user is on a desktop browser.
	 */
	onDesktop?: () => void;
};

/**
 * Tries to open the mobile app via deep link.
 * Falls back to optional `onFailed` callback if app is not installed.
 */
function useDeeplinkAttempt(path: string, options: DeeplinkOptions = {}) {
	const { delayMs = 1500, disabled = false, onFailed, onDesktop } = options;
	const timerRef = useRef<number | null>(null);
	const pageHiddenRef = useRef(false);

	useEffect(() => {
		if (disabled) return;

		const userAgent = navigator.userAgent || navigator.vendor;

		const isAndroid = /android/i.test(userAgent);
		const isIOS =
			/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

		// If not mobile, handle desktop scenario
		if (!isAndroid && !isIOS) {
			onDesktop?.();
			return;
		}

		const scheme = env.appUrl.includes("staging")
			? "medplaner-staging://"
			: "medplaner://";
		const deepLinkUrl = `${scheme}${path}`;

		function handleVisibilityChange() {
			if (document.hidden) {
				pageHiddenRef.current = true;
				timerRef.current && clearTimeout(timerRef.current);
			}
		}

		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Attempt to open the app
		window.location.href = deepLinkUrl;

		timerRef.current = window.setTimeout(() => {
			// If page is still visible after delay, app did not open
			if (!pageHiddenRef.current) {
				onFailed?.();
			}
		}, delayMs);

		return () => {
			timerRef.current && clearTimeout(timerRef.current);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [path, disabled, delayMs, onFailed, onDesktop]);
}

export default useDeeplinkAttempt;
